import React, { useEffect, useState, useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { loadScheduleTypeParams, loadDocumentScheduleParams, saveDocumentScheduleParams } from '../actionCreators';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Settings from '@mui/icons-material/Settings';
import OfferPaymentPlanScheduleParamsComponent from './OfferPaymentPlanScheduleParamsComponent';
import { shape, required, items } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';
import { initialState } from '../reducers/documentScheduleParamsReducer';
import reducer from '../reducers/documentScheduleParamsReducer';
import { CollapseCard } from '@totalsoft/rocket-ui';

export const validatorScheduleParam = items(shape({ paramValue: required }));

export const OfferPaymentPlanScheduleParamsContainer = ({
  documentId,
  siteId,
  actions,
  scheduleTypeParams,
  documentScheduleParams,
  saving,
  isLoaded
}) => {
  const [localState, dispatch] = useReducer(reducer, initialState);
  const { t } = useTranslation();

  const [dirtyInfo, setDirtyInfo, resetDirtyInfo] = useDirtyInfo();
  const [scheduleParamTypeId, setScheduleParamTypeId] = useState();

  const [validationScheduleParam, validateScheduleParam] = useDirtyFieldValidation(validatorScheduleParam, dirtyInfo);

  const handleChangeScheduleParamType = useCallback(
    value => {
      setScheduleParamTypeId(value);
      var scheduleParamTypeIdx = localState.documentScheduleParams.findIndex(x => x.scheduleTypeParamId === value);
      if (scheduleParamTypeIdx === -1) {
        var scheduleTypeParam = scheduleTypeParams?.find(x => x.scheduleTypeParamId === value);
        dispatch({
          type: 'OnDocumentScheduleParamAdd',
          payload: {
            value,
            typeParamDisplayName: scheduleTypeParam.typeParamDisplayName,
            typeParamName: scheduleTypeParam.typeParamName,
            paramTypeCode: scheduleTypeParam.paramTypeCode
          }
        });
      } else window.confirm(t('OfferEdit.ScheduleParams.ScheduleParamExists'));
      setScheduleParamTypeId(null);
    },
    [scheduleTypeParams, localState.documentScheduleParams, t]
  );

  const handlePropertyChange = useCallback(
    (scheduleTypeParamId, value) => {
      setDirtyInfo('documentScheduleParams');
      dispatch({ type: 'OnPropertyChanged', payload: { scheduleTypeParamId, value } });
    },
    [setDirtyInfo]
  );

  const handleParamDelete = value => {
    setDirtyInfo('documentScheduleParams');
    dispatch({ type: 'OnDocumentScheduleParamsDelete', payload: { value } });
  };

  const handleSave = useCallback(async () => {
    if (!validateScheduleParam(localState.documentScheduleParams)) return;

    const saveData = {
      documentId: documentId,
      siteId: siteId,
      documentScheduleParams: [...localState.documentScheduleParams]
    };

    await actions.saveDocumentScheduleParams(saveData);

    resetDirtyInfo();
  }, [actions, documentId, siteId, validateScheduleParam, localState.documentScheduleParams, resetDirtyInfo]);

  useEffect(() => {
    if (isLoaded) dispatch({ type: 'OnInitState', documentScheduleParams });
  }, [isLoaded, documentScheduleParams]);

  useEffect(() => {
    actions.loadScheduleTypeParams(documentId, siteId);
    actions.loadDocumentScheduleParams(documentId, siteId);
  }, [actions, documentId, siteId]);

  useEffect(() => {
    validateScheduleParam(localState.documentScheduleParams, dirtyInfo);
  }, [localState.documentScheduleParams, dirtyInfo, validateScheduleParam]);

  return (
    <CollapseCard id={'offerPaymentPlanScheduleParamsCard'} icon={Settings} title={t('OfferEdit.ScheduleParams.Title')} toggleOnHeaderClick>
      <OfferPaymentPlanScheduleParamsComponent
        scheduleTypeParams={scheduleTypeParams}
        onChangeScheduleParamType={handleChangeScheduleParamType}
        scheduleParamTypeId={scheduleParamTypeId}
        list={localState.documentScheduleParams || []}
        onSave={handleSave}
        onDelete={handleParamDelete}
        saving={saving}
        onPropertyChange={handlePropertyChange}
        validationScheduleParam={validationScheduleParam}
        dirtyInfo={dirtyInfo}
      />
    </CollapseCard>
  );
};

OfferPaymentPlanScheduleParamsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  actions: PropTypes.object.isRequired,
  isLoaded: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  scheduleTypeParams: PropTypes.array.isRequired,
  documentScheduleParams: PropTypes.array.isRequired
};

export function mapStateToProps(state) {
  const { typeParams } = state.currentOffer.paymentPlanData.scheduleTypeParams;
  const { saving, saved, paramList, isLoaded } = state.currentOffer.paymentPlanData.documentScheduleParams;
  return {
    scheduleTypeParams: typeParams,
    documentScheduleParams: paramList,
    saving,
    saved,
    isLoaded
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadScheduleTypeParams,
        loadDocumentScheduleParams,
        saveDocumentScheduleParams
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(OfferPaymentPlanScheduleParamsContainer);
